/**
 * LogView.jsx - Tracking > Log time & expenses: one week at a time, like an
 * engineer's E-sheet. Pick the week and the person; each day is a row -
 * PO line, T&L / Fixed, task, NT / OT / DT, Expense day - and the day's
 * expenses, sold (and cost, for the cost area) as the server worked them out.
 *   - "All people" shows the week as a grid of everyone's hours; a name opens
 *     their week.
 *   - Quick fill: Mon-Fri 8 h, copy last week; the Split button turns a
 *     day's hours into NT / OT / DT (weekday 8 + OT, Saturday OT, Sunday DT).
 *   - Changes stay on screen (marked, counted) until Save; a day emptied of
 *     hours and expense is removed on Save. Bad lines are named before
 *     anything is sent.
 *   - Paste rows from Excel (Date, Engineer, PO line, Task, NT, OT, DT,
 *     Expense) and receipts are below the grid.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Btn, GREEN, MUTED, NumInput, Section, SelectInput, TextInput, inputStyle, td, th } from '../quotes/quoteUi';
import { addTimeEntry, deleteTimeEntry, hours, importTime, money, saveTimeEntry } from '../costs/costApi';
import { addDays, dowOf, mondayOf, shortDate, weekDays } from './trackingInsights';
import { EXPENSE_COLS, EXPENSE_LABELS, Empty, Hint, parsePastedRows, splitDay } from './trackingUi';
import ReceiptsSection from './ReceiptsSection';

const ALL = '__all';
const lc = (s) =>
  String(s || '')
    .trim()
    .toLowerCase();
const n = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const FIELDS = [
  'date',
  'poLine',
  'poStatus',
  'poDescription',
  'task',
  'nt',
  'ot',
  'dt',
  'expense',
  'vendorInvoiceNo',
  'vendorInvoiceAmount',
  'note',
];
const str = (v) => (v === null || v === undefined ? '' : String(v));
const sig = (l) =>
  JSON.stringify(
    FIELDS.map((k) =>
      k === 'expense' ? !!l[k] : ['nt', 'ot', 'dt', 'vendorInvoiceAmount'].includes(k) ? (str(l[k]) === '' ? '' : n(l[k])) : str(l[k])
    )
  );
let seq = 0;
const blankLine = (date, d = {}) => ({
  key: `n${++seq}`,
  date,
  poLine: d.poLine || '',
  poStatus: '',
  poDescription: d.poDescription || 'T&L',
  task: '',
  nt: '',
  ot: '',
  dt: '',
  expense: false,
  vendorInvoiceNo: '',
  vendorInvoiceAmount: '',
  note: '',
});
const lineOf = (e) => {
  const l = {
    key: `e${e.id}`,
    id: e.id,
    date: e.date,
    poLine: e.poLine || '',
    poStatus: e.poStatus || '',
    poDescription: e.poDescription || 'T&L',
    task: e.task || '',
    nt: e.nt ? String(e.nt) : '',
    ot: e.ot ? String(e.ot) : '',
    dt: e.dt ? String(e.dt) : '',
    expense: !!e.expense,
    vendorInvoiceNo: e.vendorInvoiceNo || '',
    vendorInvoiceAmount: e.vendorInvoiceAmount === null || e.vendorInvoiceAmount === undefined ? '' : String(e.vendorInvoiceAmount),
    note: e.note || '',
  };
  return { ...l, orig: sig(l) };
};
const hoursOf = (l) => n(l.nt) + n(l.ot) + n(l.dt);
const hasContent = (l) => hoursOf(l) > 0 || l.expense;
const changed = (l) => (l.id ? sig(l) !== l.orig : hasContent(l));

/** The week's lines for one person: their saved rows, a blank line on each day without one. */
function linesFor(entries, who, days) {
  const out = [];
  for (const d of days) {
    const mine = entries.filter((e) => lc(e.person) === lc(who) && e.date === d);
    if (mine.length) for (const e of mine) out.push(lineOf(e));
    else out.push(blankLine(d));
  }
  return out;
}

/** What is wrong with a line about to be saved, or ''. */
export function lineError(l, tasks) {
  const h = ['nt', 'ot', 'dt'].map((k) => n(l[k]));
  if (h.some((x) => x < 0 || x > 24)) return 'NT, OT and DT are 0 to 24 hours.';
  if (hoursOf(l) > 24) return 'A day has at most 24 hours (NT + OT + DT).';
  if (hasContent(l) && !l.task) return 'Pick the task.';
  if (l.task && !tasks.some((t) => lc(t) === lc(l.task))) return `"${l.task}" is not on the tracking sheet's task list.`;
  return '';
}

export default function LogView({ data, projectId, editable, seesCosts, reload, onDirty, today, go }) {
  const t = data.tracking;
  const tasks = (data.lists && data.lists.tasks) || [];
  const people = data.people || [];
  const poOptions = (data.poLines || []).map((l) => l.label);
  const lastEntry = useMemo(
    () => [...(data.entries || [])].sort((a, b) => String(b.date).localeCompare(String(a.date)))[0] || null,
    [data.entries]
  );
  const [who, setWhoState] = useState(() => (people.length > 1 ? ALL : (people[0] && people[0].name) || ''));
  const [monday, setMondayState] = useState(() => mondayOf(today));
  const days = useMemo(() => weekDays(monday), [monday]);
  const [lines, setLines] = useState([]);
  const [open, setOpen] = useState({}); // key -> more details shown
  const [errors, setErrors] = useState({});
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [paste, setPaste] = useState('');
  const [showPaste, setShowPaste] = useState(false);
  const mineEntries = (data.entries || []).filter((e) => lc(e.person) === lc(who));
  const recentOfMine = [...mineEntries].sort((a, b) => String(b.date).localeCompare(String(a.date)))[0];
  const [defaults, setDefaults] = useState({ task: '', poLine: '', poDescription: 'T&L' });

  // a fresh draft whenever the week, the person or the saved rows change
  useEffect(() => {
    if (who && who !== ALL) setLines(linesFor(data.entries || [], who, days));
    else setLines([]);
    setErrors({});
    setOpen({});
  }, [data.entries, who, days]);
  useEffect(() => {
    const r = recentOfMine;
    setDefaults({
      task: (r && r.task) || '',
      poLine: (r && r.poLine) || poOptions[0] || '',
      poDescription: (r && r.poDescription) || 'T&L',
    });
    // the person's latest line seeds the quick-fill defaults when the person changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [who]);

  const pending = lines.filter(changed);
  const dirty = pending.length > 0;
  useEffect(() => {
    if (onDirty) onDirty(dirty);
  }, [dirty, onDirty]);
  const leave = useCallback(
    () => !dirty || window.confirm(`Discard ${pending.length} unsaved change${pending.length === 1 ? '' : 's'}?`),
    [dirty, pending.length]
  );
  const setWho = (v) => {
    if (v !== who && leave()) {
      setWhoState(v);
      setMsg(null);
    }
  };
  const setMonday = (v) => {
    if (v !== monday && leave()) {
      setMondayState(v);
      setMsg(null);
    }
  };

  const rowOf = useMemo(() => new Map(t.rows.map((r) => [r.id, r])), [t.rows]);
  const set = (key, patch) => {
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
    setErrors((e) => (e[key] ? { ...e, [key]: '' } : e));
  };
  const addLine = (date) =>
    setLines((ls) => {
      const at = ls.map((l) => l.date).lastIndexOf(date);
      const next = [...ls];
      next.splice(at + 1, 0, blankLine(date, defaults));
      return next;
    });
  const removeLine = (key) =>
    setLines((ls) => {
      const l = ls.find((x) => x.key === key);
      if (!l) return ls;
      if (l.id) return ls.map((x) => (x.key === key ? { ...x, nt: '', ot: '', dt: '', expense: false } : x)); // removed on Save
      const others = ls.filter((x) => x.date === l.date && x.key !== key);
      return others.length ? ls.filter((x) => x.key !== key) : ls.map((x) => (x.key === key ? blankLine(l.date) : x));
    });
  const split = (key) => setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...strH(splitDay(hoursOf(l), l.date)) } : l)));

  // quick fill
  const blankDay = (d, ls) => ls.filter((l) => l.date === d).every((l) => !l.id && !hasContent(l));
  const fillWeekdays = () => {
    if (!defaults.task) {
      setMsg({ kind: 'error', text: 'Pick the task new lines get (above the grid) first.' });
      return;
    }
    let filled = 0;
    const next = lines.map((l) => {
      const g = new Date(`${l.date}T00:00:00Z`).getUTCDay();
      if (g === 0 || g === 6 || !blankDay(l.date, lines)) return l;
      filled += 1;
      return { ...l, nt: '8', ot: '', dt: '', task: defaults.task, poLine: defaults.poLine, poDescription: defaults.poDescription };
    });
    setLines(next);
    setMsg({
      kind: 'ok',
      text: filled ? `Filled ${filled} weekday(s) with 8 h NT - check them, then Save week.` : 'Every weekday already has something on it.',
    });
  };
  const copyLastWeek = () => {
    const prev = new Set(weekDays(addDays(monday, -7)));
    const src = (data.entries || []).filter((e) => lc(e.person) === lc(who) && prev.has(e.date));
    if (!src.length) {
      setMsg({ kind: 'error', text: `${who} has nothing logged in the week of ${shortDate(addDays(monday, -7), true)}.` });
      return;
    }
    let copied = 0;
    let out = [...lines];
    for (const d of days) {
      const from = src.filter((e) => e.date === addDays(d, -7));
      if (!from.length || !blankDay(d, out)) continue;
      const fresh = from.map((e) => ({ ...lineOf({ ...e, date: d }), key: `n${++seq}`, id: undefined, orig: undefined }));
      const i = out.findIndex((l) => l.date === d);
      out = [...out.slice(0, i), ...fresh, ...out.slice(i).filter((l) => l.date !== d)];
      copied += fresh.length;
    }
    setLines(out);
    setMsg({
      kind: 'ok',
      text: copied
        ? `Copied ${copied} line(s) from last week onto the empty days - check them, then Save week.`
        : "This week's days are not empty - nothing copied.",
    });
  };

  const save = async () => {
    const errs = {};
    for (const l of pending) {
      const e = lineError(l, tasks);
      if (e) errs[l.key] = e;
    }
    // a day: at most 24 hours over its lines
    for (const d of days) {
      const sum = lines.filter((l) => l.date === d).reduce((a, l) => a + hoursOf(l), 0);
      if (sum > 24)
        for (const l of lines.filter((x) => x.date === d && changed(x)))
          errs[l.key] = errs[l.key] || `${dowOf(d)} has ${sum} hours over its lines - at most 24.`;
    }
    setErrors(errs);
    if (Object.keys(errs).length) {
      setMsg({ kind: 'error', text: `${Object.keys(errs).length} line(s) need a fix before saving (marked in red).` });
      return;
    }
    setBusy(true);
    let added = 0;
    let edited = 0;
    let removed = 0;
    const failed = [];
    for (const l of pending) {
      const body = { ...Object.fromEntries(FIELDS.map((k) => [k, l[k]])), person: who };
      let r;
      if (l.id && !hasContent(l)) {
        r = await deleteTimeEntry(projectId, l.id);
        if (r.ok) removed += 1;
      } else if (l.id) {
        r = await saveTimeEntry(projectId, l.id, body);
        if (r.ok) edited += 1;
      } else {
        r = await addTimeEntry(projectId, body);
        if (r.ok) added += 1;
      }
      if (!r.ok) failed.push(`${shortDate(l.date)}: ${(r.body && r.body.error) || 'not saved'}`);
    }
    await reload();
    setBusy(false);
    const done = [added && `${added} added`, edited && `${edited} changed`, removed && `${removed} removed`].filter(Boolean).join(', ');
    setMsg(
      failed.length
        ? { kind: 'error', text: `${done ? `Saved (${done}), but ` : ''}${failed.length} line(s) were not saved - ${failed.join('; ')}` }
        : { kind: 'ok', text: `Saved: ${done || 'nothing to change'}.` }
    );
  };
  const discard = () => {
    setLines(linesFor(data.entries || [], who, days));
    setErrors({});
    setMsg(null);
  };

  const pasted = parsePastedRows(paste);
  const importPaste = async () => {
    setBusy(true);
    const r = await importTime(projectId, pasted);
    setBusy(false);
    if (!r.ok) {
      setMsg({ kind: 'error', text: (r.body && r.body.error) || 'The rows were not imported.' });
      return;
    }
    setPaste('');
    setShowPaste(false);
    setMsg({ kind: 'ok', text: `${r.body.imported} row(s) imported.` });
    await reload();
  };

  const person = people.find((p) => lc(p.name) === lc(who));
  const weekTotals = lines.reduce(
    (a, l) => {
      const saved = l.id && !changed(l) ? rowOf.get(l.id) : null;
      a.nt += n(l.nt);
      a.ot += n(l.ot);
      a.dt += n(l.dt);
      if (saved) {
        a.exp += saved.expenseTotal;
        a.sell += saved.sell;
        a.cost += n(saved.cost);
      }
      return a;
    },
    { nt: 0, ot: 0, dt: 0, exp: 0, sell: 0, cost: 0 }
  );
  const label = `${shortDate(days[0])} - ${shortDate(days[6], true)}`;
  const inWeek = (d) => d >= days[0] && d <= days[6];
  const jumpTo = lastEntry && !inWeek(lastEntry.date) && !(data.entries || []).some((e) => inWeek(e.date)) ? lastEntry : null;

  if (!people.length) {
    return (
      <Section title="Log time & expenses">
        <Empty
          text="Nobody on this job yet - add the people who will log time, with their rates."
          action="Add people in Setup"
          onAction={() => go('setup:people')}
        />
      </Section>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginBottom: 12 }}>
        <SelectInput
          aria-label="Person"
          value={who}
          onChange={setWho}
          style={{ width: 210, fontWeight: 700 }}
          options={[
            ...(people.length > 1 ? [{ value: ALL, label: 'All people (week overview)' }] : []),
            ...people.map((p) => ({ value: p.name, label: p.name })),
          ]}
        />
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <Btn small aria-label="Previous week" onClick={() => setMonday(addDays(monday, -7))}>
            &lsaquo;
          </Btn>
          <span aria-live="polite" style={{ fontSize: 12.5, fontWeight: 800, minWidth: 150, textAlign: 'center' }}>
            Week of {label}
          </span>
          <Btn small aria-label="Next week" onClick={() => setMonday(addDays(monday, 7))}>
            &rsaquo;
          </Btn>
        </span>
        <Btn small onClick={() => setMonday(mondayOf(today))}>
          This week
        </Btn>
        <input
          type="date"
          aria-label="Go to the week of"
          value=""
          onChange={(e) => {
            if (e.target.value) setMonday(mondayOf(e.target.value));
          }}
          style={{ ...inputStyle, width: 140 }}
        />
        {jumpTo && (
          <button
            type="button"
            onClick={() => setMonday(mondayOf(jumpTo.date))}
            style={{
              border: 0,
              background: 'none',
              color: GREEN,
              fontWeight: 800,
              fontSize: 11.5,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Last logged: {shortDate(jumpTo.date, true)} &rarr;
          </button>
        )}
      </div>

      {msg && (
        <div
          role={msg.kind === 'error' ? 'alert' : 'status'}
          style={{
            marginBottom: 10,
            padding: '8px 12px',
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 600,
            background: msg.kind === 'error' ? '#FFEBEE' : '#E8F5E9',
            color: msg.kind === 'error' ? '#B71C1C' : GREEN,
          }}
        >
          {msg.text}
        </div>
      )}

      {who === ALL ? (
        <WeekOverview data={data} days={days} open={(name) => setWho(name)} />
      ) : (
        <Section
          title={
            <>
              {who} - hours and expense days{' '}
              <Hint
                title="Hours and expense days"
                tip="One line per day as on the engineer's E-sheet. NT = normal time, OT = overtime (x1.3 with OT Multi?), DT = double time (x1.7). Ticking Expense day adds the person's per-day expenses; on a Travel day also half the round-trip airfare."
                from="Setup > People & rates (rates and per-day amounts)"
              />
            </>
          }
          right={
            editable && (
              <span style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                {dirty && (
                  <span role="status" style={{ fontSize: 11.5, fontWeight: 800, color: '#B45309' }}>
                    {pending.length} unsaved
                  </span>
                )}
                {dirty && (
                  <Btn small disabled={busy} onClick={discard}>
                    Discard
                  </Btn>
                )}
                <Btn primary disabled={!dirty || busy} onClick={save}>
                  {busy ? 'Saving...' : 'Save week'}
                </Btn>
              </span>
            )
          }
        >
          {editable && (
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 8,
                alignItems: 'center',
                background: '#F6F8F3',
                borderRadius: 8,
                padding: '8px 10px',
                marginBottom: 10,
              }}
            >
              <span style={{ fontSize: 10.5, fontWeight: 800, color: MUTED, textTransform: 'uppercase' }}>New lines</span>
              <SelectInput
                aria-label="Default task"
                value={defaults.task}
                blank="Task..."
                options={tasks}
                style={{ width: 150 }}
                onChange={(v) => setDefaults((d) => ({ ...d, task: v }))}
              />
              <SelectInput
                aria-label="Default PO line"
                value={defaults.poLine}
                blank="PO line..."
                options={poOptions}
                style={{ width: 150 }}
                onChange={(v) => setDefaults((d) => ({ ...d, poLine: v }))}
              />
              <SelectInput
                aria-label="Default T&L or Fixed"
                value={defaults.poDescription}
                options={['T&L', 'Fixed']}
                style={{ width: 80 }}
                onChange={(v) => setDefaults((d) => ({ ...d, poDescription: v }))}
              />
              <span style={{ width: 1, height: 20, background: '#D6DCCB' }} />
              <Btn small onClick={fillWeekdays} title="Every empty Monday-Friday gets 8 h NT with the task and PO line on the left">
                Fill Mon-Fri 8 h
              </Btn>
              <Btn small onClick={copyLastWeek} title="Last week's lines onto this week's empty days">
                Copy last week
              </Btn>
            </div>
          )}
          {person && (
            <div style={{ fontSize: 11, color: MUTED, marginBottom: 8 }}>
              An Expense day adds for {who}:{' '}
              {EXPENSE_COLS.filter((k) => k !== 'airfare' && n(person.perDay[k]))
                .map((k) => `${EXPENSE_LABELS[k]} ${money(person.perDay[k])}`)
                .join(' · ') || 'nothing yet'}
              {n(person.perDay.airfare) ? ` · on a Travel day half the ${money(person.perDay.airfare)} airfare` : ''}.{' '}
              <button
                type="button"
                onClick={() => go('setup:people')}
                style={{
                  border: 0,
                  background: 'none',
                  padding: 0,
                  color: GREEN,
                  fontWeight: 700,
                  fontSize: 11,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                Change in Setup
              </button>
            </div>
          )}
          <div style={{ overflowX: 'auto' }}>
            <table aria-label={`Week of ${who}`} style={{ width: '100%', borderCollapse: 'collapse', minWidth: 860 }}>
              <thead>
                <tr>
                  {[
                    'Day',
                    'PO line',
                    'T&L / Fixed',
                    'Task',
                    'NT',
                    'OT',
                    'DT',
                    'Expense day',
                    'Expenses',
                    'Sold',
                    ...(seesCosts ? ['Cost'] : []),
                    '',
                  ].map((x, i) => (
                    <th key={`${x}${i}`} style={{ ...th, textAlign: i >= 4 && i <= 6 ? 'right' : i >= 8 ? 'right' : 'left' }}>
                      {x}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {lines.map((l, idx) => {
                  const first = idx === 0 || lines[idx - 1].date !== l.date;
                  const ch = changed(l);
                  const err = errors[l.key];
                  const saved = l.id && !ch ? rowOf.get(l.id) : null;
                  const weekend = ['Sat', 'Sun'].includes(dowOf(l.date));
                  const gone = l.id && !hasContent(l);
                  const cols = seesCosts ? 12 : 11;
                  const bg = err ? '#FFF5F5' : gone ? '#FFF8E1' : ch ? '#FFFDE7' : weekend ? '#FAFAF7' : undefined;
                  return [
                    <tr key={l.key} style={{ background: bg, borderTop: first && idx ? '2px solid #EEF1EA' : undefined }}>
                      <td
                        style={{
                          ...td,
                          fontSize: 12,
                          fontWeight: first ? 700 : 400,
                          color: first ? '#1F2328' : MUTED,
                          whiteSpace: 'nowrap',
                          width: 92,
                        }}
                      >
                        {first ? `${dowOf(l.date)} ${shortDate(l.date)}` : '  +'}
                        {ch && (
                          <span
                            title={gone ? 'Removed on Save' : 'Not saved yet'}
                            style={{ marginLeft: 4, color: '#B45309', fontWeight: 900 }}
                          >
                            •
                          </span>
                        )}
                      </td>
                      <td style={{ ...td, width: 130 }}>
                        <SelectInput
                          aria-label={`${l.date} PO line`}
                          value={l.poLine}
                          blank="-"
                          options={poOptions}
                          disabled={!editable}
                          onChange={(v) => set(l.key, { poLine: v })}
                          style={hasContent(l) && !l.poLine && poOptions.length ? { borderColor: '#FBC02D' } : undefined}
                        />
                      </td>
                      <td style={{ ...td, width: 80 }}>
                        <SelectInput
                          aria-label={`${l.date} T&L or Fixed`}
                          value={l.poDescription}
                          options={['T&L', 'Fixed']}
                          disabled={!editable}
                          onChange={(v) => set(l.key, { poDescription: v })}
                        />
                      </td>
                      <td style={{ ...td, width: 140 }}>
                        <SelectInput
                          aria-label={`${l.date} task`}
                          value={l.task}
                          blank="-"
                          options={tasks}
                          disabled={!editable}
                          onChange={(v) => set(l.key, { task: v })}
                          style={err && hasContent(l) && !l.task ? { borderColor: '#E53935' } : undefined}
                        />
                      </td>
                      {['nt', 'ot', 'dt'].map((k) => (
                        <td key={k} style={{ ...td, width: 58 }}>
                          <NumInput
                            aria-label={`${l.date} ${k.toUpperCase()}`}
                            value={l[k]}
                            disabled={!editable}
                            onChange={(v) => set(l.key, { [k]: v })}
                            placeholder="0"
                          />
                        </td>
                      ))}
                      <td style={{ ...td, textAlign: 'center', width: 70 }}>
                        <input
                          type="checkbox"
                          aria-label={`${l.date} expense day`}
                          checked={!!l.expense}
                          disabled={!editable}
                          onChange={(e) => set(l.key, { expense: e.target.checked })}
                        />
                      </td>
                      <td
                        style={{ ...td, fontSize: 11.5, textAlign: 'right', width: 86, color: saved ? '#1F2328' : MUTED }}
                        title={
                          saved
                            ? EXPENSE_COLS.filter((k) => saved.expenses[k])
                                .map((k) => `${EXPENSE_LABELS[k]} ${money(saved.expenses[k])}`)
                                .join(', ')
                            : 'Worked out on Save'
                        }
                      >
                        {saved ? (saved.expenseTotal ? money(saved.expenseTotal) : '-') : ch && l.expense ? 'on Save' : '-'}
                      </td>
                      <td style={{ ...td, fontSize: 11.5, textAlign: 'right', width: 90, color: saved ? '#1F2328' : MUTED }}>
                        {saved ? money(saved.sell) : ch && hoursOf(l) ? 'on Save' : '-'}
                      </td>
                      {seesCosts && (
                        <td style={{ ...td, fontSize: 11.5, textAlign: 'right', width: 86, color: MUTED }}>
                          {saved ? money(saved.cost) : '-'}
                        </td>
                      )}
                      <td style={{ ...td, whiteSpace: 'nowrap', width: 120, textAlign: 'right' }}>
                        {editable && (
                          <span style={{ display: 'inline-flex', gap: 3 }}>
                            <Btn
                              small
                              title="Split the hours into NT / OT / DT: weekday 8 + OT, Saturday OT, Sunday DT"
                              aria-label={`Split ${l.date}`}
                              disabled={!hoursOf(l)}
                              onClick={() => split(l.key)}
                            >
                              Split
                            </Btn>
                            <Btn
                              small
                              title="Another line on this day (another task or PO line)"
                              aria-label={`Add a line on ${l.date}`}
                              onClick={() => addLine(l.date)}
                            >
                              +
                            </Btn>
                            <Btn
                              small
                              title="PO status, vendor invoice, note"
                              aria-label={`More on ${l.date}`}
                              aria-expanded={!!open[l.key]}
                              onClick={() => setOpen((o) => ({ ...o, [l.key]: !o[l.key] }))}
                            >
                              &hellip;
                            </Btn>
                            <Btn
                              small
                              danger
                              title={l.id ? 'Remove this day (on Save)' : 'Clear'}
                              aria-label={`Clear ${l.date}`}
                              disabled={!l.id && !hasContent(l)}
                              onClick={() => removeLine(l.key)}
                            >
                              x
                            </Btn>
                          </span>
                        )}
                      </td>
                    </tr>,
                    open[l.key] && (
                      <tr key={`${l.key}-more`} style={{ background: bg }}>
                        <td />
                        <td colSpan={cols - 1} style={{ ...td, paddingBottom: 8 }}>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8 }}>
                            <label style={mini}>
                              PO status
                              <TextInput
                                aria-label={`${l.date} PO status`}
                                value={l.poStatus}
                                disabled={!editable}
                                onChange={(v) => set(l.key, { poStatus: v })}
                              />
                            </label>
                            <label style={mini}>
                              Vendor invoice #
                              <TextInput
                                aria-label={`${l.date} vendor invoice`}
                                value={l.vendorInvoiceNo}
                                disabled={!editable}
                                onChange={(v) => set(l.key, { vendorInvoiceNo: v })}
                              />
                            </label>
                            <label style={mini}>
                              Vendor amount ($)
                              <NumInput
                                aria-label={`${l.date} vendor amount`}
                                value={l.vendorInvoiceAmount}
                                disabled={!editable}
                                onChange={(v) => set(l.key, { vendorInvoiceAmount: v })}
                              />
                            </label>
                            <label style={mini}>
                              Note
                              <TextInput
                                aria-label={`${l.date} note`}
                                value={l.note}
                                disabled={!editable}
                                onChange={(v) => set(l.key, { note: v })}
                              />
                            </label>
                          </div>
                        </td>
                      </tr>
                    ),
                    err && (
                      <tr key={`${l.key}-err`} style={{ background: bg }}>
                        <td />
                        <td colSpan={cols - 1} role="alert" style={{ ...td, fontSize: 11.5, fontWeight: 700, color: '#B71C1C' }}>
                          {err}
                        </td>
                      </tr>
                    ),
                  ];
                })}
                <tr style={{ background: '#F6F8F3' }}>
                  <td style={{ ...td, fontSize: 12, fontWeight: 800 }} colSpan={4}>
                    Week
                  </td>
                  {['nt', 'ot', 'dt'].map((k) => (
                    <td key={k} style={{ ...td, fontSize: 12, fontWeight: 800, textAlign: 'right' }}>
                      {hours(weekTotals[k])}
                    </td>
                  ))}
                  <td />
                  <td style={{ ...td, fontSize: 12, fontWeight: 800, textAlign: 'right' }}>{money(weekTotals.exp)}</td>
                  <td style={{ ...td, fontSize: 12, fontWeight: 800, textAlign: 'right' }}>{money(weekTotals.sell)}</td>
                  {seesCosts && <td style={{ ...td, fontSize: 12, fontWeight: 800, textAlign: 'right' }}>{money(weekTotals.cost)}</td>}
                  <td style={{ ...td, fontSize: 10.5, color: MUTED, textAlign: 'right' }}>{dirty ? 'saved figures only' : ''}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {editable && (
        <Section
          title="Paste rows from Excel"
          right={
            <Btn small onClick={() => setShowPaste((v) => !v)} aria-expanded={showPaste}>
              {showPaste ? 'Close' : 'Paste rows'}
            </Btn>
          }
          hint="Copy rows from a tracking sheet or timesheet: Date, Engineer, PO line, Task, NT, OT, DT, Expense (Yes / No). A header row is skipped."
        >
          {showPaste && (
            <>
              <textarea
                aria-label="Paste day rows"
                rows={4}
                value={paste}
                onChange={(e) => setPaste(e.target.value)}
                style={{ ...inputStyle, resize: 'vertical', fontFamily: 'monospace' }}
                placeholder={'2026-08-03\tAlex Able\tPO-1 L1\tMod-Build\t8\t0\t0\tNo'}
              />
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
                <span style={{ fontSize: 11, color: MUTED }}>{pasted.length ? `${pasted.length} row(s) read` : 'Nothing pasted yet.'}</span>
                <Btn small primary disabled={!pasted.length || busy} onClick={importPaste}>
                  Import
                </Btn>
              </div>
            </>
          )}
        </Section>
      )}

      <ReceiptsSection data={data} projectId={projectId} editable={editable} reload={reload} defaultPerson={who === ALL ? '' : who} />
    </div>
  );
}

const mini = { display: 'flex', flexDirection: 'column', gap: 3, fontSize: 10, fontWeight: 700, color: MUTED, textTransform: 'uppercase' };
const strH = (h) => ({ nt: h.nt ? String(h.nt) : '', ot: h.ot ? String(h.ot) : '', dt: h.dt ? String(h.dt) : '' });

/** Everyone's week at a glance: hours per day, an "E" on an expense day; a name opens that person's week. */
function WeekOverview({ data, days, open }) {
  const people = data.people || [];
  const rows = data.tracking.rows || [];
  const cell = (name, d) => rows.filter((r) => lc(r.person) === lc(name) && r.date === d);
  const total = (list) => list.reduce((a, r) => a + r.hours, 0);
  const any = rows.some((r) => days.includes(r.date));
  return (
    <Section
      title={
        <>
          Week overview{' '}
          <Hint
            title="Week overview"
            tip="Everyone's hours on each day of this week. E = an expense day. Pick a name to log or change that person's days."
            from="Log time & expenses"
          />
        </>
      }
    >
      {!any && (
        <div style={{ marginBottom: 8 }}>
          <Empty text="Nobody has logged anything this week. Pick a person to log their days." />
        </div>
      )}
      <div style={{ overflowX: 'auto' }}>
        <table aria-label="Week overview" style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
          <thead>
            <tr>
              <th style={th}>Person</th>
              {days.map((d) => (
                <th key={d} style={{ ...th, textAlign: 'right' }}>
                  {dowOf(d)} {shortDate(d)}
                </th>
              ))}
              <th style={{ ...th, textAlign: 'right' }}>Week</th>
            </tr>
          </thead>
          <tbody>
            {people.map((p) => {
              const week = days.flatMap((d) => cell(p.name, d));
              return (
                <tr key={p.name}>
                  <td style={{ ...td, fontSize: 12 }}>
                    <button
                      type="button"
                      onClick={() => open(p.name)}
                      style={{
                        border: 0,
                        background: 'none',
                        padding: 0,
                        color: GREEN,
                        fontWeight: 800,
                        fontSize: 12,
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        textAlign: 'left',
                      }}
                    >
                      {p.name}
                    </button>
                  </td>
                  {days.map((d) => {
                    const c = cell(p.name, d);
                    const h = total(c);
                    return (
                      <td
                        key={d}
                        style={{
                          ...td,
                          fontSize: 12,
                          textAlign: 'right',
                          color: h ? '#1F2328' : '#C4C9BD',
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {h ? hours(h) : '-'}
                        {c.some((r) => r.expense) && (
                          <span title="Expense day" style={{ marginLeft: 3, fontSize: 9.5, fontWeight: 800, color: '#B45309' }}>
                            E
                          </span>
                        )}
                      </td>
                    );
                  })}
                  <td style={{ ...td, fontSize: 12, fontWeight: 800, textAlign: 'right' }}>{hours(total(week))}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Section>
  );
}
